// JobDetail "Warranty / Follow-up Visits" card.
//
// Visible when the job is installed-or-later OR it already has ≥1 warranty
// visit (so visits stay manageable after a legal status revert/disqualify —
// they otherwise orphan with no UI handle). The "Schedule warranty visit"
// button is gated on installed-or-later only; the list stays editable
// whenever visits exist.
//
// Warranty workdays on site = SUM over visits of (end-start+1), computed
// client-side via the shared warrantyWorkdaysTotal — the single definition.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck, Plus } from 'lucide-react';
import {
  type JobStatus,
  type WarrantyVisitRow,
  warrantyWorkdays,
  warrantyWorkdaysTotal,
} from '@gutterinv/shared';
import { getJobWarrantyVisits } from '../../api/client.js';
import { Card } from '../ui/card.js';
import { Button } from '../ui/button.js';
import { cn } from '../../lib/utils.js';
import { formatDate } from '../../lib/format.js';
import { WarrantyVisitModal } from '../WarrantyVisitModal.js';

const SCHEDULABLE: JobStatus[] = ['installed', 'invoiced', 'completed'];

function rangeLabel(v: WarrantyVisitRow): string {
  return v.visitDateStart === v.visitDateEnd
    ? formatDate(v.visitDateStart)
    : `${formatDate(v.visitDateStart)} – ${formatDate(v.visitDateEnd)}`;
}

export function WarrantyVisitsCard({
  jobId,
  jobStatus,
  embedded = false,
}: {
  jobId: string;
  jobStatus: JobStatus;
  /** When true, render as a divided sub-section (no own Card) — e.g. folded
   *  into the Schedule card below the Estimate/Install columns. */
  embedded?: boolean;
}) {
  const canSchedule = SCHEDULABLE.includes(jobStatus);
  const [modal, setModal] = useState<{ visit: WarrantyVisitRow | null } | null>(null);

  const { data: visits } = useQuery({
    queryKey: ['job', jobId, 'warranty'],
    queryFn: () => getJobWarrantyVisits(jobId),
  });

  const list = visits ?? [];

  // Self-gate: render nothing when warranty can't be scheduled AND none exist.
  // (Below-installed jobs can still hold orphaned visits after a revert — those
  // keep the card visible so they remain editable/deletable.)
  if (!canSchedule && list.length === 0) return null;

  const totalWorkdays = warrantyWorkdaysTotal(list);

  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <h3 className="eyebrow flex items-center gap-1.5">
          <ShieldCheck className="h-3 w-3" />
          Warranty / Follow-up Visits
        </h3>
        {canSchedule && (
          <Button size="sm" variant="outline" onClick={() => setModal({ visit: null })}>
            <Plus className="h-3.5 w-3.5" />
            Schedule warranty visit
          </Button>
        )}
      </div>

      {list.length > 0 ? (
        <>
          <p className="mt-2 text-sm text-foreground">
            Warranty workdays on site: <span className="font-semibold">{totalWorkdays}</span>
          </p>
          <div className="mt-2 divide-y divide-border rounded-md border border-border">
            {list.map((v) => {
              const days = warrantyWorkdays(v.visitDateStart, v.visitDateEnd);
              const isDone = !!v.completedAt;
              return (
                <button
                  key={v.id}
                  onClick={() => setModal({ visit: v })}
                  className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left transition-colors hover:bg-primary/[0.06]"
                >
                  <div className="min-w-0 flex-1">
                    <div
                      className={cn(
                        'text-sm font-medium text-foreground',
                        isDone && 'line-through opacity-60',
                      )}
                    >
                      {rangeLabel(v)}
                      <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                        ({days} {days === 1 ? 'day' : 'days'})
                      </span>
                    </div>
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">
                      {v.people.length > 0
                        ? v.people.map((p) => p.name).join(', ')
                        : '(no people yet)'}
                      {v.notes && (
                        <>
                          <span className="mx-1.5 text-taupe">·</span>
                          {v.notes}
                        </>
                      )}
                    </div>
                  </div>
                  {isDone && <span className="shrink-0 text-xs text-muted-foreground">Done</span>}
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <p className="mt-2 text-sm text-muted-foreground">
          No warranty visits scheduled. Use “Schedule warranty visit” to add one.
        </p>
      )}

      {modal && (
        <WarrantyVisitModal
          jobId={jobId}
          visit={modal.visit}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );

  return embedded ? (
    <div className="mt-4 border-t border-border pt-4">{body}</div>
  ) : (
    <Card className="p-5">{body}</Card>
  );
}
